import { getPlayUrl } from '@/services/ConsoleRequest';
import React, { useEffect, useRef } from 'react';
import { useRequest } from 'umi';
import { v4 as uuidv4 } from 'uuid';

interface Props {
  url: string;
  currentTime?: number;
  recordRequest?: (playSeconds: number, durationTime: number, logParams?: any) => Promise<any>; // 埋点请求函数，playSeconds是当前播放进度（秒），durationTime是本次增量播放时长（秒，从上次上报到本次上报）
  autoPlay?: boolean;
  reportInterval?: number; // 上报间隔时间（毫秒），默认 10000ms（10秒）
  logParams?: any;
}

// 生成唯一的UUID
const generateUUID = (): string => {
  return `ckplayer-${uuidv4()}`;
};

const VideoPlayer: React.FC<Props> = ({
  url,
  currentTime,
  recordRequest,
  autoPlay = false,
  reportInterval = 10000, // 默认每10秒上报一次
  logParams,
}) => {
  // 生成唯一的容器ID（在组件生命周期内保持不变）
  const containerIdRef = useRef<string>(generateUUID());
  const containerId = containerIdRef.current;

  // 定时器引用
  const reportTimerRef = useRef<NodeJS.Timeout | null>(null);
  // 累计播放时长（秒）
  const accumulatedDurationRef = useRef<number>(0);
  // 当前播放进度（秒）
  const currentPlayTimeRef = useRef<number>(0);
  // 播放器实例引用
  const playerRef = useRef<any>(null);
  // 开始播放的时间戳（毫秒）
  const playStartTimeRef = useRef<number>(0);
  const [videoDuration, setVideoDuration] = React.useState<number>(0);

  const { run: getPlayUrlRequest } = useRequest(
    (playUrl: string) => {
      return getPlayUrl(playUrl);
    },
    {
      manual: true,
      onSuccess: (res: any) => {
        initPlayer(res);
      },
    },
  );

  // 开始定期上报
  const startReporting = () => {
    // 清除已存在的定时器
    stopReporting();

    console.log('🚀 开始定期上报埋点');
    reportTimerRef.current = setInterval(() => {
      reportData(0);
    }, reportInterval);
  };

  // 停止定期上报
  const stopReporting = () => {
    if (reportTimerRef.current) {
      console.log('⏹ 停止定期上报埋点');
      clearInterval(reportTimerRef.current);
      reportTimerRef.current = null;
    }
  };

  // 上报数据
  const reportData = (playEnd: number) => {
    console.log(`📢 准备上报埋点数据 playEnd: ${playEnd}`);
    if (!recordRequest) return;

    const currentSeconds = currentPlayTimeRef.current;

    // 计算本次增量播放时长：从上次上报到现在经过的实际时间（秒）
    const now = Date.now();
    const durationTime = playStartTimeRef.current > 0 ? (now - playStartTimeRef.current) / 1000 : 0;

    // 如果播放时长小于等于0，则不上报
    if (durationTime <= 0) {
      console.log('⏭ 跳过上报：本次播放时长为0');
      return;
    }

    // 更新累计播放时长（用于内部统计）
    accumulatedDurationRef.current += durationTime;

    console.log(
      '📊 上报埋点数据:',
      `当前播放秒数：${currentSeconds}秒`,
      `当前播放时长：${durationTime.toFixed(2)}秒`,
      `累计播放时长：${accumulatedDurationRef.current.toFixed(2)}秒`,
    );

    // 调用上报接口：传递增量值，而不是累计值
    recordRequest(currentSeconds, durationTime, { ...logParams, playEnd })
      .then(() => {
        console.log('✅ 埋点上报成功');
        // 重置开始时间，为下一次计时做准备
        playStartTimeRef.current = Date.now();
      })
      .catch((error) => {
        console.error('❌ 埋点上报失败:', error);
        // 即使上报失败，也要重置开始时间
        playStartTimeRef.current = Date.now();
      });
  };

  useEffect(() => {
    if (url) {
      if (url.endsWith('m3u8')) {
        getPlayUrlRequest(url);
      } else {
        initPlayer(url);
      }

      // 清理函数
      return () => {
        console.log('销毁播放器');
        // 停止上报定时器
        stopReporting();
        // 销毁播放器实例 - X3 版本
        if (playerRef.current) {
          try {
            // X3 版本销毁方法
            if (typeof playerRef.current.remove === 'function') {
              playerRef.current.remove();
            } else if (typeof playerRef.current.destroy === 'function') {
              playerRef.current.destroy();
            }
          } catch (e) {
            console.error('销毁播放器失败:', e);
          }
          playerRef.current = null;
        }
        // 清空容器
        const videoPlayElement = document.getElementById(containerId);
        if (videoPlayElement) {
          videoPlayElement.innerHTML = '';
        }
      };
    }
  }, [url]);

  // 自动保存
  const initPlayer = (videoUrl: string) => {
    try {
      // 获取 ckplayer 对象（从全局 window 对象中）
      const ckplayer = (window as any).ckplayer;

      // 检查 ckplayer 是否加载
      if (!ckplayer) {
        // 重试机制：如果未加载，等待 1 秒后重试
        setTimeout(() => {
          initPlayer(videoUrl);
        }, 1000);
        return;
      }

      console.log('✅ ckplayer X3 已成功加载！');

      // 本地环境使用固定视频地址
      const isLocalEnv =
        process.env.UMI_ENV === 'dev' ||
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1';
      const finalVideoUrl = isLocalEnv
        ? 'https://eduvod.fanruan.com/asset/edac03146ed6bedd71434e6170594aa8/play_video/78c9043223da61fd0ee04e8fb88707aa.mp4'
        : videoUrl;

      if (isLocalEnv) {
        console.log('🔧 本地环境：使用固定测试视频地址');
      }

      // 创建播放器配置 - X3 版本（根据官方文档）
      const videoObject = {
        variable: 'player',
        video: finalVideoUrl, // 视频源
        seek: currentTime || 0, // 默认跳转时间（秒）
        barHideTime: 1500, // 控制栏隐藏时间（毫秒）
        plug: 'hls.js', //设置使用hls插件
        container: `#${containerId}`, // 使用唯一的容器 ID
        autoplay: autoPlay, //是否自动播放
      };

      console.log('🎬 播放器配置:', videoObject);

      // 创建播放器实例 - X3 方式
      const player = new ckplayer(videoObject);
      playerRef.current = player;
      console.log('✅ 播放器实例创建成功');
      console.log('📺 播放器实例:', player);

      // 初始化播放时间
      currentPlayTimeRef.current = currentTime || 0;

      player.loadedMetaData(function (metaData: any) {
        console.log('📊 元数据:', metaData);
        if (metaData && metaData.duration) {
          setVideoDuration(metaData.duration);
        }
      });

      player.play(function () {
        console.log('▶ 视频开始播放');
        // 记录开始播放的时间戳
        playStartTimeRef.current = Date.now();
        // 开始定期上报
        startReporting();
      });

      player.pause(function () {
        console.log('⏸ 视频暂停播放');
        // 停止定期上报
        stopReporting();
        // 暂停时立即上报一次当前数据
        if (recordRequest && playStartTimeRef.current > 0) {
          const playEnd = playStartTimeRef.current >= videoDuration;
          reportData(playEnd ? 1 : 0);
        }
        // 清除开始时间戳，停止计时
        playStartTimeRef.current = 0;
      });

      player.ended(function () {
        console.log('⏹ 视频播放结束');
        // 停止定期上报
        stopReporting();
        // 播放结束时立即上报一次当前数据
        if (recordRequest && playStartTimeRef.current > 0) {
          reportData(1);
        }
        // 清除开始时间戳
        playStartTimeRef.current = 0;
      });

      player.time(function (playSeconds: number) {
        // 更新当前播放时间
        currentPlayTimeRef.current = playSeconds;
        // console.log('当前播放时间:', playSeconds);
      });

      player.error(function (error: any) {
        console.error('✗ 播放器错误:', error);
        // 出错时停止上报
        stopReporting();
      });

      console.log('✓ 播放器初始化完成 (X3 版本)');
    } catch (error) {
      console.error('✗ 初始化ckplayer失败:', error);
      // 捕获详细的错误信息
      if (error instanceof Error) {
        console.error('错误详情:', {
          message: error.message,
          stack: error.stack,
        });
      }
    }
  };

  return <div id={containerId} style={{ width: '100%', height: '100%' }}></div>;
};

export default VideoPlayer;
